import { useState } from 'react';
import { Eye, EyeOff, ArrowDownLeft, ArrowUpRight, TrendingUp, TrendingDown } from 'lucide-react';
import { Button } from '@/components/ui/button';

export const BalanceHero = ({ mainBalance = 0, netPnl = 0, activeInvestment = 0, pnlLabel = 'All-time', onDeposit, onWithdraw }) => {
  const [hidden, setHidden] = useState(false);
  const positive = netPnl >= 0;
  const pnlPct = activeInvestment > 0 ? (netPnl / activeInvestment) * 100 : 0;

  const mask = '••••••';
  const formatted = `$${Number(mainBalance).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  return (
    <div
      data-testid="balance-hero"
      className="relative overflow-hidden rounded-2xl border border-brand-border bg-gradient-to-br from-white via-brand-primary-subtle/40 to-white p-5 sm:p-7 shadow-sm"
    >
      {/* subtle grain / accent dot */}
      <div className="absolute top-0 right-0 w-40 h-40 bg-brand-primary/[0.04] rounded-full blur-3xl pointer-events-none" />
      <div className="relative">
        <div className="flex items-center justify-between gap-3">
          <p className="text-[11px] uppercase tracking-[0.18em] font-semibold text-brand-text-secondary">
            Main Balance
          </p>
          <button
            onClick={() => setHidden((v) => !v)}
            className="w-9 h-9 rounded-full bg-white/70 hover:bg-white border border-brand-border flex items-center justify-center text-brand-text-secondary transition-all active:scale-95"
            data-testid="balance-eye-toggle"
            aria-label={hidden ? 'Show balance' : 'Hide balance'}
          >
            {hidden ? <EyeOff size={16} /> : <Eye size={16} />}
          </button>
        </div>
        <div className="mt-2 flex items-baseline gap-3 flex-wrap">
          <h1 className="font-heading text-4xl sm:text-5xl font-extrabold tracking-tight text-brand-text-primary" data-testid="balance-hero-value">
            {hidden ? mask : formatted}
          </h1>
          {!hidden && (
            <span
              className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold ${positive ? 'bg-brand-success/10 text-brand-success' : 'bg-brand-error/10 text-brand-error'}`}
              data-testid="balance-pnl-pill"
            >
              {positive ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
              {positive ? '+' : ''}{pnlPct.toFixed(2)}%
            </span>
          )}
        </div>
        <p className="mt-1 text-xs text-brand-text-secondary">
          {pnlLabel} net P&L {hidden ? '—' : <span className={positive ? 'text-brand-success font-medium' : 'text-brand-error font-medium'}>{positive ? '+' : ''}${Math.abs(netPnl).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>}
        </p>

        <div className="mt-5 flex gap-2.5">
          <Button
            onClick={onDeposit}
            className="flex-1 h-11 rounded-xl bg-brand-primary hover:bg-brand-primary-hover text-white text-sm font-semibold shadow-sm"
            data-testid="balance-hero-deposit"
          >
            <ArrowDownLeft size={16} className="mr-1.5" /> Deposit
          </Button>
          <Button
            onClick={onWithdraw}
            variant="outline"
            className="flex-1 h-11 rounded-xl border-brand-primary text-brand-primary hover:bg-brand-primary-subtle text-sm font-semibold"
            data-testid="balance-hero-withdraw"
          >
            <ArrowUpRight size={16} className="mr-1.5" /> Withdraw
          </Button>
        </div>
      </div>
    </div>
  );
};
